import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { AuthSplash } from "./AuthSplash";
import { LOGIN_PATH } from "./paths";
import {
  clearPostAuthIntent,
  hasWelcomeIntent,
  readPendingInvitation,
} from "./postAuthIntent";

/* The post-auth forwarding unit: where an authenticated viewer goes next, and
 * how (SPA navigation vs full browser redirect). Shared by the /login,
 * /check-email, and /signup gates — it lives here, independent of any one
 * route, so none of them has to import a sibling route. */

/* Where to send an authenticated viewer that arrived via the `redirect` param.
 * Only same-origin relative paths are honored (this app's pages or a sibling
 * app's under the same domain) — a single leading slash, but NOT
 * "//host", "/\\host" (some browsers fold "/\" to "//"), or a scheme — so the
 * param can't be used as an open redirect. Anything else (or a loop back to
 * /login) falls back to "/". */
function safeRedirect(raw: string | null): string {
  if (!raw) return "/";

  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return "/";
  }

  const isRelative =
    decoded.startsWith("/") &&
    !decoded.startsWith("//") &&
    !decoded.startsWith("/\\");
  if (!isRelative) return "/";

  if (decoded === LOGIN_PATH || decoded.startsWith(`${LOGIN_PATH}?`))
    return "/";

  return decoded;
}

function isHomeTarget(target: string): boolean {
  const pathAndQuery = target.split("#")[0];
  const q = pathAndQuery.indexOf("?");
  if ((q === -1 ? pathAndQuery : pathAndQuery.slice(0, q)) !== "/")
    return false;

  const query = q === -1 ? "" : pathAndQuery.slice(q + 1);
  return !new URLSearchParams(query).has("invitation_id");
}

export type PostAuthDestination = { via: "router" | "browser"; to: string };

export function nextAfterAuth(search: string): PostAuthDestination {
  const params = new URLSearchParams(search);

  // Storage fallback: the sign-up screen strips invitation_id out of the URL so a
  // marketing tag can't read the capability token off the page URL, stashing it
  // instead (see postAuthIntent). The query stays the fast path.
  const invitationId = params.get("invitation_id") ?? readPendingInvitation();
  if (invitationId) {
    clearPostAuthIntent();
    return {
      via: "router",
      to: `/invite/${encodeURIComponent(invitationId)}`,
    };
  }

  const redirect = params.get("redirect");
  const redirectTo = redirect ? safeRedirect(redirect) : null;

  // A redirect that names somewhere other than home is a real deep link, and
  // wins outright.
  if (redirectTo && !isHomeTarget(redirectTo)) {
    clearPostAuthIntent();
    return { via: "browser", to: redirectTo };
  }

  if (params.get("next") === "welcome" || hasWelcomeIntent()) {
    clearPostAuthIntent();
    return { via: "router", to: "/welcome" };
  }

  // Home-equivalent redirect, no first-run intent: hand the browser back the
  // exact URL it was headed for (query intact), as before.
  if (redirectTo) {
    clearPostAuthIntent();
    return { via: "browser", to: redirectTo };
  }

  return { via: "router", to: "/" };
}

/** Renders the post-auth hop for a destination computed by nextAfterAuth:
 *  in-SPA <Navigate> for this app's own pages, a full window.location.replace
 *  (splash while the browser unloads) for `redirect` targets that may belong
 *  to a sibling app on the same domain. replace — not assign — mirrors
 *  <Navigate replace>: back should not return to the login/verify page. */
export function ForwardAfterAuth({ search }: { search: string }) {
  const { via, to } = nextAfterAuth(search);

  useEffect(() => {
    if (via === "browser") window.location.replace(to);
  }, [via, to]);

  if (via === "browser") return <AuthSplash />;

  return <Navigate to={to} replace />;
}
